import React, { useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";
import { ConfigSchema, SchemaField } from "./configTypes";
import {
  attentionItems,
  canConfirm,
  describeAge,
  EMPTY_DISPLAY,
  FieldGroup,
  formatAttr,
  freshnessEnabled,
  freshnessFor,
  groupFields,
  isEditable,
  isFixedAfterCreate,
  optionFor,
} from "./configModel";
import { capabilityLadder } from "./capability";
import { Vehicle } from "./data";
import { ConfigEditor } from "./useConfigEditor";
import { reviewLabel } from "./ConfigActionBar";
import FieldControl, { isSegmented } from "./FieldControl";
import FieldFreshness from "./FieldFreshness";
import ReviewDialog from "./ReviewDialog";
import ValueChip from "./ValueChip";
import { tokens } from "./tokens";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";

/**
 * The tractor's config record: what DynamoDB holds, and where it is changed.
 *
 * The whole surface is generated from `GET /v1/config-schema` — fields,
 * grouping, order, labels, help, enum vocabularies, staleness thresholds, and
 * the per-field `can_edit` resolved for this caller. Nothing about the
 * vocabulary is written down on this side, so the console cannot offer an edit
 * the API would reject, and a field added to `tractor_config.py` appears here
 * on the next deploy of the backend alone.
 *
 * **The record is an accordion, not a wall.** Nineteen fields across six groups
 * is a page and a half of scrolling to reach the one line you came to change,
 * and almost all of it is stable — nobody re-reads the customer asset ID. So
 * groups collapse, and the ones that open by themselves are the ones with
 * something wrong in them (a stale approval, a missing confirmation stamp) plus
 * the headline facts, which never collapse because they are why you opened the
 * page. Everything else is one click.
 *
 * Two consequences that had to be handled rather than accepted:
 *
 *   * a pending edit inside a *closed* group would be invisible at save time,
 *     so a group's header carries a count of what is pending inside it;
 *   * a validation error inside a closed group would block Review with no
 *     visible cause, so an error opens its group.
 *
 * The edit session itself lives in `useConfigEditor`, shared with the action bar
 * pinned to the top of the page — see that module for the save contract. The
 * bar owns Edit / Discard / Review; this card keeps only a footer copy of them
 * for the bottom of a long form, in the same words.
 */
interface ConfigPanelProps {
  vehicle: Vehicle;
  schema: ConfigSchema | null;
  editor: ConfigEditor;
  now?: number;
  /** Page-level controls for this card's header (the maximise toggle). */
  actions?: React.ReactNode;
  /**
   * Pin the header while the card scrolls inside itself — only when the card
   * is the scroll container (maximised). In the flowing layout the page's own
   * scroller would catch it, and it would stick under the action bar instead.
   */
  stickyHeader?: boolean;
}

/**
 * Why a field has no control in edit mode — on the one occasion that is worth
 * saying out loud.
 *
 * Three kinds of field have no control, and only one of them leaves the reader
 * with anything to do about it: "somebody else can change this, you cannot". The
 * other two are permanent properties of the record — identity, fixed when the
 * tractor was onboarded, and provenance, which the API maintains — and neither
 * is news to anyone looking at a display name or a version number. Badging them
 * tagged a third of the fields on screen with a word that answers a question
 * nobody asked, and the noise made the badge that *does* matter easy to miss.
 * Those fields now just show their value, which was always the whole story.
 *
 * Returns null when there is nothing to say.
 */
const lockedNote = (
  field: SchemaField,
): { label: string; reason: string } | null => {
  if (isFixedAfterCreate(field) || field.system) return null;
  // Locked with nobody named to ask. Rendering the empty half of the sentence
  // ("… only") would be worse than saying nothing.
  if (!field.editable_by.length) return null;
  return {
    label: `${field.editable_by.join("/")} only`,
    reason: `Editable by ${field.editable_by.join(" / ")}.`,
  };
};

const clock = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isNaN(t)
    ? iso
    : new Date(t).toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });
};

const ConfigPanel: React.FC<ConfigPanelProps> = ({
  vehicle,
  schema,
  editor,
  now = Date.now(),
  actions,
  stickyHeader = false,
}) => {
  const fields = useMemo(() => schema?.fields ?? [], [schema]);
  const groups = useMemo(
    () => groupFields(fields, undefined, schema?.groups),
    [fields, schema],
  );

  // The group whose heading the schema deliberately left empty is the tractor's
  // headline — it renders open, above the accordion, with no header of its own.
  const headline = groups.find((g) => !g.label);
  const collapsible = useMemo(() => groups.filter((g) => g.label), [groups]);

  // The schema decides whether freshness chrome is drawn at all; with it off,
  // nothing is "asking to be looked at" and no group opens itself for it.
  const freshness = freshnessEnabled(schema);
  const attention = useMemo(
    () => (freshness ? attentionItems(fields, vehicle.attrs, now) : []),
    [freshness, fields, vehicle.attrs, now],
  );

  /**
   * Where each capability rung sits, and how far up this tractor has got — the
   * same derivation the fleet table sorts and draws its pips from, so the two
   * views cannot disagree about which approval is the highest one held.
   *
   * Read against the *stored* record rather than the draft: mid-edit, the rail
   * describes what is approved, not what someone is proposing.
   */
  const rungStates = useMemo(
    () =>
      new Map(
        capabilityLadder(schema, vehicle.attrs).rungs.map((r) => [
          r.field.name,
          r.state,
        ]),
      ),
    [schema, vehicle.attrs],
  );

  // Opened by default: whatever is asking to be looked at. A quiet tractor
  // opens nothing, which is the point — the record is closed until it isn't.
  const defaultOpen = useMemo(() => {
    const wanted = new Set(attention.map((a) => a.field.group));
    if (!vehicle.hasConfigRecord) {
      collapsible.forEach((g) => {
        if (g.fields.some((f) => f.group === "capability")) wanted.add(g.key);
      });
    }
    return [...wanted];
  }, [attention, collapsible, vehicle.hasConfigRecord]);

  const [open, setOpen] = useState<string[]>(defaultOpen);

  // Re-seed when the tractor changes: the rail switches vehicles in place, and
  // carrying one tractor's open groups onto the next says nothing true.
  useEffect(() => setOpen(defaultOpen), [vehicle.vehicleName]); // eslint-disable-line react-hooks/exhaustive-deps

  // An error inside a collapsed group would disable Review with nothing on
  // screen to explain it. Open the group instead of leaving it to be found.
  const errorKeys = Object.keys(editor.errors).join(",");
  useEffect(() => {
    const names = errorKeys ? errorKeys.split(",") : [];
    if (!names.length) return;
    const holding = collapsible
      .filter((g) => g.fields.some((f) => names.includes(f.name)))
      .map((g) => g.key);
    setOpen((prev) => [...new Set([...prev, ...holding])]);
  }, [errorKeys, collapsible]);

  // A restored draft has pending edits somewhere in the record; open the
  // groups that hold them so "restored your changes" points at something.
  const pendingGroupKeys = Object.keys(editor.pendingByGroup).join(",");
  useEffect(() => {
    if (!editor.restoredAt) return;
    const keys = pendingGroupKeys ? pendingGroupKeys.split(",") : [];
    if (keys.length) setOpen((prev) => [...new Set([...prev, ...keys])]);
  }, [editor.restoredAt, pendingGroupKeys]);

  if (!schema) {
    return (
      <>
        <Header>
          <SectionLabel>Tractor config</SectionLabel>
          {actions && <HeaderActions>{actions}</HeaderActions>}
        </Header>
        <Empty>
          The field list hasn’t loaded yet, so this record can’t be shown.
        </Empty>
      </>
    );
  }

  const allOpen = collapsible.length > 0 && open.length === collapsible.length;

  // Only says something when there is something to say. Where the row is stored
  // is this console's own plumbing, not the reader's business — an unapproved
  // record is.
  const subtitle = [
    vehicle.hasConfigRecord
      ? null
      : "No approval record yet — capability fields can’t be trusted.",
    vehicle.configVersion !== null ? `v${vehicle.configVersion}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const renderFields = (group: FieldGroup) => (
    // A ladder has to be read down one column. Flowed across three, the rail
    // segments land beside each other instead of on top of each other and stop
    // being a rail at all — which is the whole reason the capability group gets
    // an exception to the record's column rhythm.
    <FieldList $ladder={group.fields.some((f) => rungStates.has(f.name))}>
      {group.fields.map((field) => {
        const value = vehicle.attrs[field.name] ?? null;
        const fresh = freshnessFor(field, vehicle.attrs, now);
        const editable = editor.editing && isEditable(field);
        const locked = editor.editing && !editable ? lockedNote(field) : null;
        // The same rail the fleet table draws, on the same fields, meaning the
        // same thing: green up to the highest approval held, grey above it. The
        // capability group is a ladder wherever it is rendered, so clicking a
        // tractor whose row reads "◐ Auto testing" lands on a page that says so
        // in the same shape rather than in four flat rows.
        const rungState = rungStates.get(field.name);
        const controlId = `config-${field.name}`;
        const labelId = `${controlId}-label`;
        const errorId = `${controlId}-error`;
        const helpId = `${controlId}-help`;
        const error = editor.errors[field.name];
        // Short enums render as a radiogroup, which a <label for> cannot
        // reach; those are labelled by id instead.
        const draftValue = editor.draft[field.name] ?? "";
        const segmented = isSegmented(
          field,
          typeof draftValue === "string" ? draftValue : "",
        );
        const chosen =
          field.type === "enum" && typeof draftValue === "string"
            ? optionFor(field, draftValue)
            : undefined;
        // A value the save will flip because of another edit in this draft
        // says so under the control, where the operator is looking, before
        // Review repeats it.
        const cascade = editor.cascades.find(
          (c) => c.field.name === field.name,
        );
        const helpText =
          editable && !error
            ? cascade
              ? `Becomes ${formatAttr(field, cascade.to) || EMPTY_DISPLAY} on save — ${cascade.rule}.`
              : (chosen?.help ?? field.help)
            : undefined;
        const canReconfirm =
          !editor.editing &&
          editor.canEdit &&
          isEditable(field) &&
          canConfirm(field, vehicle.attrs);
        return (
          <FieldRow
            key={field.name}
            $wide={field.type === "text"}
            $rung={rungState}
          >
            <FieldHead>
              <FieldLabel
                as={editable && !segmented ? "label" : "div"}
                id={labelId}
                htmlFor={editable && !segmented ? controlId : undefined}
              >
                {field.label}
              </FieldLabel>
              {/*
                No `sheet-synced` badge here. It marked three roster fields on
                every read of every tractor to describe a migration detail — the
                Google Sheet sync that still owns them — which is not what
                someone opening this record is trying to find out. The warning
                that matters is the one in the review dialog, at the moment an
                edit to one of those fields is about to be made and could be
                overwritten by the next sync.
              */}
              {locked && (
                <Hint text={locked.reason}>
                  <Badge tone="neutral">{locked.label}</Badge>
                </Hint>
              )}
              {editor.dirtyNames.has(field.name) && (
                <Badge tone="count">edited</Badge>
              )}
            </FieldHead>

            {editable ? (
              <>
                <FieldControl
                  field={field}
                  value={draftValue}
                  error={error}
                  onChange={(next) => editor.updateField(field, next)}
                  onBlur={() => editor.blurField(field)}
                  labelledBy={segmented ? labelId : undefined}
                  describedBy={error ? errorId : helpText ? helpId : undefined}
                  blocked={editor.blockedFor(field)}
                />
                {error && <FieldError id={errorId}>{error}</FieldError>}
                {helpText && <FieldHelp id={helpId}>{helpText}</FieldHelp>}
                {canConfirm(field, vehicle.attrs) && (
                  <ConfirmRow>
                    <input
                      id={`confirm-${field.name}`}
                      type="checkbox"
                      checked={editor.confirmChecked(field.name)}
                      disabled={editor.dirtyNames.has(field.name)}
                      onChange={(e) =>
                        editor.toggleConfirm(field.name, e.target.checked)
                      }
                    />
                    <ConfirmLabel htmlFor={`confirm-${field.name}`}>
                      {editor.dirtyNames.has(field.name)
                        ? "Changing the value already records a fresh confirmation."
                        : !freshness || fresh.age === null
                          ? "I re-checked this today and it’s still correct"
                          : `I re-checked this today and it’s still correct (last confirmed ${describeAge(fresh.age)})`}
                    </ConfirmLabel>
                  </ConfirmRow>
                )}
              </>
            ) : (
              <FieldValue>
                <ValueChip
                  field={field}
                  value={value}
                  plain={field.type === "text"}
                  // Prose reads as prose: notes were one ellipsed line here,
                  // with the full text only in a hover title.
                  wrap={field.type === "text"}
                />
                {/* Shared with the fleet table's row expansion — see
                    FieldFreshness for why unconfirmed is a note, not a badge. */}
                {freshness && (
                  <FieldFreshness
                    field={field}
                    fresh={fresh}
                    hint={(text, child) => <Hint text={text}>{child}</Hint>}
                  />
                )}
                {/* The routine action this record exists for — "I checked,
                    it's still true" — used to require knowing to press Edit and
                    find a checkbox. It starts here, on the value it is about. */}
                {canReconfirm && (
                  <Button
                    variant="quiet"
                    size="sm"
                    onClick={() => editor.startWithConfirm(field.name)}
                  >
                    Re-confirm
                  </Button>
                )}
              </FieldValue>
            )}
          </FieldRow>
        );
      })}
    </FieldList>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <>
        <Header $sticky={stickyHeader}>
          <div>
            <SectionLabel>Tractor config</SectionLabel>
            {subtitle && <Sub>{subtitle}</Sub>}
          </div>
          <HeaderActions>
            {collapsible.length > 0 && (
              <Button
                variant="quiet"
                size="sm"
                onClick={() =>
                  setOpen(allOpen ? [] : collapsible.map((g) => g.key))
                }
              >
                {allOpen ? "Collapse all" : "Expand all"}
              </Button>
            )}
            {!editor.editing && editor.blocked && (
              <Hint text={editor.blocked}>
                <ReadOnly>Read only</ReadOnly>
              </Hint>
            )}
            {actions}
          </HeaderActions>
        </Header>

        {editor.restoredAt && (
          <Banner $tone="info" role="status">
            <strong>Restored the changes you hadn’t saved</strong> (from{" "}
            {clock(editor.restoredAt)}). Review them, or discard them.
            <Dismiss type="button" onClick={editor.dismissNotes}>
              Dismiss
            </Dismiss>
          </Banner>
        )}
        {editor.droppedDraft && (
          <Banner $tone="warn" role="status">
            An unsaved draft from earlier was dropped, because this record has
            changed since it was written.
            <Dismiss type="button" onClick={editor.dismissNotes}>
              Dismiss
            </Dismiss>
          </Banner>
        )}

        {editor.conflictFields !== null && (
          <Banner $tone="warn" role="status">
            <strong>This tractor changed while you were editing.</strong>{" "}
            {editor.conflictFields.length
              ? `${editor.conflictFields.join(", ")} moved underneath your edit. Your changes are still here, now compared against the current values — review and save again.`
              : "The row was updated by someone else, but none of the fields you touched changed. Review and save again."}
            {editor.refreshedFields.length > 0 &&
              ` Fields you hadn’t touched now show the current values (${editor.refreshedFields.join(", ")}).`}
          </Banner>
        )}
        {editor.conflictFields === null &&
          editor.refreshedFields.length > 0 && (
            <Banner $tone="info" role="status">
              This record was refreshed while you were editing. Fields you
              hadn’t touched now show the current values (
              {editor.refreshedFields.join(", ")}); your edits are unchanged.
            </Banner>
          )}

        {/* Suppressed while the dialog is open — it shows the same message
            inline, and rendering it twice reads as two separate failures. */}
        {editor.saveError && !editor.reviewing && (
          <Banner $tone="bad" role="alert">
            {editor.saveError}
          </Banner>
        )}

        {headline && <Headline>{renderFields(headline)}</Headline>}

        <Accordion type="multiple" value={open} onValueChange={setOpen}>
          {collapsible.map((group) => {
            const pending = editor.pendingByGroup[group.key] ?? 0;
            const flagged = attention.filter(
              (a) => a.field.group === group.key,
            ).length;
            return (
              <AccordionItem key={group.key} value={group.key}>
                <AccordionTrigger>
                  <GroupLabel>{group.label}</GroupLabel>
                  <GroupCount>
                    {group.fields.length}
                    {group.fields.length === 1 ? " field" : " fields"}
                  </GroupCount>
                  {flagged > 0 && <Badge tone="warn">{flagged} to check</Badge>}
                  {pending > 0 && <Badge tone="count">{pending} pending</Badge>}
                </AccordionTrigger>
                <AccordionContent>
                  {group.note && <GroupNote>{group.note}</GroupNote>}
                  {renderFields(group)}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {editor.editing && (
          <Footer>
            <FooterNote>
              {editor.pendingCount === 0
                ? "No changes yet."
                : `${editor.pendingCount} change${editor.pendingCount === 1 ? "" : "s"} pending. Saved together, or not at all.`}
            </FooterNote>
            <Button variant="ghost" size="sm" onClick={editor.requestCancel}>
              Discard changes
            </Button>
            <Button
              size="sm"
              onClick={editor.openReview}
              disabled={editor.pendingCount === 0 || editor.hasErrors}
            >
              {reviewLabel(editor.pendingCount)}
            </Button>
          </Footer>
        )}

        {editor.reviewing && (
          <ReviewDialog
            changes={editor.list}
            cascades={editor.cascades}
            confirmations={editor.confirmations}
            vehicleLabel={vehicle.displayName ?? vehicle.vehicleName}
            saving={editor.saving}
            error={editor.saveError}
            onConfirm={editor.submit}
            onCancel={editor.closeReview}
            effectiveAt={editor.effectiveAt}
            onEffectiveAtChange={editor.setEffectiveAt}
            note={editor.note}
            onNoteChange={editor.setNote}
            noteRequired={editor.noteRequired}
            tz={editor.tz}
            laterChangeAt={editor.laterChangeAt}
          />
        )}
      </>
    </TooltipProvider>
  );
};

/**
 * A short explanation attached to a badge. Radix rather than `title`: the
 * native tooltip waits a second, never appears for a keyboard user, and cannot
 * hold the two-sentence answers these badges owe the reader.
 */
const Hint: React.FC<{ text: string; children: React.ReactNode }> = ({
  text,
  children,
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HintTarget tabIndex={0}>{children}</HintTarget>
    </TooltipTrigger>
    <TooltipContent>{text}</TooltipContent>
  </Tooltip>
);

const HintTarget = styled.span`
  display: inline-flex;
  border-radius: 4px;
  cursor: help;

  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

const Header = styled.div<{ $sticky?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 20px 12px;
  ${(p) =>
    p.$sticky
      ? `position: sticky;
         top: 0;
         z-index: 2;
         background: ${tokens.white};
         border-bottom: 1px solid ${tokens.line};
         border-radius: 14px 14px 0 0;`
      : ""}
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${tokens.slate};
`;

const Sub = styled.div`
  font-size: 12.5px;
  color: ${tokens.slate};
  margin-top: 4px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
`;

const ReadOnly = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.slate};
  background: ${tokens.offWhite};
  border: 1px solid ${tokens.line};
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
`;

/** The always-open group. Same padding as an accordion panel, no header. */
const Headline = styled.div`
  border-top: 1px solid ${tokens.line};
  padding: 14px 20px 4px;
`;

const GroupLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.navy};
`;

/*
 * No `flex: 1` here. It used to push the badges to the far end of the trigger,
 * which was fine in a 500px column and absurd across a 2000px card — "2 to
 * check" ended up a screen away from the group it was counting. Everything
 * clusters at the left, next to the name it describes.
 */
const GroupCount = styled.span`
  font-size: 12px;
  color: ${tokens.slate};
  margin-right: 2px;
`;

const GroupNote = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${tokens.slate};
  margin-bottom: 10px;
`;

/**
 * Fields in columns, as many as fit.
 *
 * The record is the full width of the page now, and a full-width list of
 * label-left / value-right rows is the one layout that gets *worse* the more
 * space you give it: at 2000px the label is at one edge and its value at the
 * other, and pairing them is a trip across dead space. So each field is a
 * self-contained block — label above value — and the blocks flow into however
 * many columns the viewport affords. A laptop gets two, an ultrawide four, and
 * a field added to `tractor_config.py` lands in the flow without a decision.
 *
 * Read and edit mode share this exact layout, so switching between them moves
 * nothing around; the value is simply replaced by the control that edits it.
 *
 * `min(100%, 300px)` rather than a bare 300px: on a phone the card is narrower
 * than the track's floor, and a track that cannot shrink pushed every control
 * past the card's edge. 300 rather than 320 so a 1366px laptop — where the
 * main column now scrolls inside itself and pays for its own scrollbar — still
 * gets two columns; a label over a chip does not need the extra 20px.
 */
const FieldList = styled.div<{ $ladder?: boolean }>`
  display: grid;
  /* auto-*fill*, not auto-fit: fit collapses the unused tracks and stretches
     the fields that are there, so a two-field group rendered as two 1000px
     cells while the five-field group above it used 380px ones. Fill keeps the
     column rhythm identical down the whole record and simply leaves the spare
     tracks empty. */
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 14px 28px;
  padding-bottom: 14px;
  align-items: start;

  /* The capability group is the one exception to that flow: its fields are the
     rungs of a ladder, and a ladder read across three columns is four unrelated
     bars. One column, so the rail is continuous and "how far up" is legible the
     same way it is in the fleet table. */
  ${(p) => (p.$ladder ? "grid-template-columns: minmax(0, 640px);" : "")}
`;

const RAIL: Record<string, string> = {
  held: tokens.readyText,
  conditional: "rgba(255,150,0,0.75)",
  denied: "rgba(229,72,77,0.6)",
  off: tokens.line,
  unset: tokens.line,
};

const FieldRow = styled.div<{ $wide: boolean; $rung?: string }>`
  min-width: 0;
  /* Prose (notes, the tag-up narrative) takes the whole row: it is a paragraph,
     and a paragraph in a 280px column beside a one-word enum reads as neither. */
  grid-column: ${(p) => (p.$wide ? "1 / -1" : "auto")};
  /* Capability rungs carry the ladder rail; every other field carries nothing,
     so the rail reads as "this group is a ladder" and not as decoration. */
  ${(p) =>
    p.$rung === undefined
      ? ""
      : `border-left: 3px ${p.$rung === "off" ? "dashed" : "solid"} ${
          RAIL[p.$rung] ?? tokens.line
        };
         padding-left: 11px;`}
`;

const FieldHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

const FieldLabel = styled.div`
  font-size: 13px;
  color: ${tokens.slate};
`;

const FieldValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px 10px;
  flex-wrap: wrap;
  min-width: 0;
  /* Caps prose at a readable measure even when its row spans the full width. */
  max-width: 90ch;
`;

const FieldHelp = styled.div`
  font-size: 12px;
  color: ${tokens.slate};
  margin-top: 4px;
  max-width: 68ch;
`;

const FieldError = styled.div`
  font-size: 12px;
  color: ${tokens.groundedText};
  margin-top: 4px;
`;

const ConfirmRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: 7px;

  input {
    margin: 2px 0 0;
    accent-color: ${tokens.blue};
    cursor: pointer;
  }
  input:disabled {
    cursor: default;
  }
`;

const ConfirmLabel = styled.label`
  font-size: 12px;
  color: ${tokens.slate};
  cursor: pointer;
`;

const Banner = styled.div<{ $tone: "warn" | "bad" | "info" }>`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin: 0 20px 12px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${(p) =>
    p.$tone === "bad"
      ? tokens.groundedText
      : p.$tone === "warn"
        ? tokens.majorText
        : tokens.ink};
  background: ${(p) =>
    p.$tone === "bad"
      ? "rgba(229,72,77,0.08)"
      : p.$tone === "warn"
        ? "rgba(255,150,0,0.1)"
        : "rgba(0,105,255,0.06)"};
  border: 1px solid
    ${(p) =>
      p.$tone === "bad"
        ? "rgba(229,72,77,0.35)"
        : p.$tone === "warn"
          ? "rgba(255,150,0,0.35)"
          : "rgba(0,105,255,0.25)"};
`;

const Dismiss = styled.button`
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 600;
  color: ${tokens.blue};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
    border-radius: 3px;
  }
`;

const Empty = styled.div`
  padding: 16px 20px 20px;
  font-size: 13px;
  color: ${tokens.slate};
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid ${tokens.line};
  background: ${tokens.white};
  border-radius: 0 0 14px 14px;
`;

const FooterNote = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  color: ${tokens.slate};
`;

export default ConfigPanel;
